import { RoleEnum } from '@acme/api';
import { UserStatusEnum, useUser } from '@acme/domains';
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

type RoleSlotProps = { children: React.ReactNode };
type RoleSlotComponent = React.FC<RoleSlotProps> & { role: RoleEnum };

const createRoleSlot = (
  role: RoleEnum,
  displayName: string
): RoleSlotComponent => {
  const Comp: React.FC<RoleSlotProps> = ({ children }) => <>{children}</>;
  (Comp as RoleSlotComponent).role = role;
  Comp.displayName = displayName;
  return Comp as RoleSlotComponent;
};

export const AuthClient = createRoleSlot(RoleEnum.Client, 'AuthClient');
export const AuthAdmin = createRoleSlot(RoleEnum.Admin, 'AuthAdmin');
export const AuthBank = createRoleSlot(RoleEnum.Bank, 'AuthBank');
export const AuthProvider = createRoleSlot(RoleEnum.Provider, 'AuthProvider');

type RoleSlot = {
  role: RoleEnum;
  element: React.ReactElement<RoleSlotProps>;
};

const extractRoleSlots = (node: React.ReactNode): RoleSlot[] =>
  React.Children.toArray(node).flatMap((child) => {
    if (!React.isValidElement(child)) return [];
    const role = (child.type as { role?: RoleEnum } | undefined)?.role;
    if (role == null) return [];
    return [{ role, element: child as React.ReactElement<RoleSlotProps> }];
  });

interface AuthGuardProps {
  checkFunction: () => Promise<boolean>;
  redirectToFail: string;
  children: React.ReactNode;
  requireStatus?: UserStatusEnum;
  allowedRoles?: RoleEnum | RoleEnum[];
}

export function AuthGuard({
  checkFunction,
  redirectToFail,
  children,
  requireStatus,
  allowedRoles,
}: AuthGuardProps) {
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, organization, isLoading: userLoading } = useUser();

  const childrenArray = React.Children.toArray(children);
  const wrapper =
    childrenArray.length === 1 && React.isValidElement(childrenArray[0])
      ? (childrenArray[0] as React.ReactElement)
      : null;

  const wrapperRole =
    wrapper != null
      ? (wrapper.type as { role?: RoleEnum } | undefined)?.role
      : null;

  const roleSlots =
    wrapper != null && wrapperRole == null
      ? extractRoleSlots(wrapper.props.children)
      : extractRoleSlots(children);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const isValid = await checkFunction();

        if (!isValid) {
          setIsAuthorized(false);
          navigate(redirectToFail, { replace: true });
          return;
        }

        // Ждем загрузки пользователя
        if (userLoading) {
          return;
        }
        // Проверяем роль (если задана)
        if (allowedRoles != null) {
          const allowed = Array.isArray(allowedRoles)
            ? allowedRoles
            : [allowedRoles];
          const roleId = organization?.role?.id;

          if (roleId == null || !allowed.includes(roleId as RoleEnum)) {
            setIsAuthorized(false);
            navigate('/error/403', { replace: true });
            return;
          }
        }

        // Проверяем статус пользователя
        if (user?.status !== undefined) {
          if (user.status === UserStatusEnum.Blocked) {
            setIsAuthorized(false);
            navigate('/auth/blocked-user', { replace: true });
            return;
          }

          if (
            user.status === UserStatusEnum.ExpectsChanges ||
            user.status === UserStatusEnum.AwaitingСonfirmation
          ) {
            // Пользователь авторизован, но должен находиться на странице профиля
            const currentPath = location.pathname;
            if (currentPath !== '/profile' && currentPath !== '/profile/edit') {
              navigate('/profile', { replace: true });
            }
            // Разрешаем доступ к странице профиля
            setIsAuthorized(true);
            setIsChecking(false);
            return;
          }

          if (user.status === UserStatusEnum.Confirmed) {
            setIsAuthorized(true);
            setIsChecking(false);
            return;
          }
        }

        setIsAuthorized(isValid);
      } catch (error) {
        console.error('Auth check failed:', error);
        setIsAuthorized(false);
        navigate(redirectToFail, { replace: true });
      } finally {
        setIsChecking(false);
      }
    };

    checkAuth();
  }, [
    checkFunction,
    redirectToFail,
    navigate,
    user,
    organization,
    userLoading,
    location.pathname,
    allowedRoles,
  ]);

  if (isChecking || userLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-lg">Проверка авторизации...</div>
      </div>
    );
  }

  if (!isAuthorized) return null;

  // Если переданы роль-слоты (<AuthClient/>, <AuthAdmin/>...), выбираем нужный по текущей роли.
  if (roleSlots.length > 0) {
    const roleId = organization?.role?.id as RoleEnum | undefined;
    const match = roleSlots.find((s) => s.role === roleId)?.element;
    if (!match) return <Navigate to="/error/403" replace />;

    // Если роль-слоты лежат внутри одного wrapper-компонента (например, <DefaultLayout>),
    // сохраняем wrapper и подменяем его children на выбранный слот.
    if (wrapper != null && wrapperRole == null) {
      return React.cloneElement(wrapper, wrapper.props, match.props.children);
    }

    return match.props.children;
  }

  return children;
}

export const checkToken = async (): Promise<boolean> => {
  return new Promise((resolve) => {
    const token = localStorage.getItem('token');
    resolve(!!token && token.trim() !== '');
  });
};
